/**
 * Aftermath (08 §2.9): one screen that walks the player through what the mission changed, a
 * step at a time - debrief, the memorial beat if someone was lost, promotion and perk choice,
 * medals, the situation report - each ending on a single CONTINUE. Then on to the theatre map,
 * the operation's end, the classic roster, the practice picker or the Daily Drop, by
 * `deployment.mode`. A training flight gets the course's end card instead (trainingEnd.ts).
 *
 * There is no way back from what happened: Esc does nothing here.
 */

import './aftermath.css';
import type { AftermathInput, Ctx, Screen } from '../context';
import { el, Timers } from '../dom';
import { serviceName } from '../text';
import { trainingEndScreen } from '../trainingEnd';
import { frame } from '../widgets/frame';
import { debriefStep } from './debriefStep';
import { medalsStep } from './medalsStep';
import { memorialBeat, mourned } from './memorialBeat';
import { promotionStep } from './promotionStep';
import type { Step, StepEnv, StepView } from './shell';
import { hasSummary, summaryStep } from './summaryStep';

export type StepName = 'debrief' | 'memorial' | 'promotion' | 'medals' | 'summary';

const STEPS: Readonly<Record<StepName, Step>> = {
  debrief: debriefStep,
  memorial: memorialBeat,
  promotion: promotionStep,
  medals: medalsStep,
  summary: summaryStep,
};

/** The deployed ranger did not come back. Pure. */
export function isDeceased(input: AftermathInput): boolean {
  return input.result.outcome === 'kia' || mourned(input)?.deployed === true;
}

/**
 * The steps this aftermath has, in order; those with nothing to show are left out. A dead man
 * gets no ceremony: his medals are shown on the memorial beat, as posthumous awards. Pure.
 */
export function planSteps(input: AftermathInput): StepName[] {
  const steps: StepName[] = ['debrief'];
  const a = input.aftermath;
  if (mourned(input) !== null) steps.push('memorial');
  if (a === null) return steps;
  if (!isDeceased(input)) {
    const perkOwed = a.perkChoices !== null && a.perkChoices.length > 0;
    if (a.promotedTo !== null || perkOwed) steps.push('promotion');
    if (a.medals.length > 0) steps.push('medals');
  }
  if (hasSummary(input)) steps.push('summary');
  return steps;
}

/** Where CONTINUE leads once the last step is done. */
function leave(ctx: Ctx, input: AftermathInput): void {
  const mode = input.deployment.mode;
  if (mode === 'classic') ctx.nav.classic();
  else if (mode === 'practice') ctx.nav.practice();
  else if (mode === 'daily') ctx.nav.daily();
  else if (input.aftermath !== null && input.aftermath.operationStatus !== 'active') ctx.nav.operationEnd();
  else ctx.nav.theatre();
}

export function aftermathScreen(ctx: Ctx, given: AftermathInput): Screen {
  // ROUND7 §5: the training course has no debrief - nothing was scored - but a card with the rules that matter.
  if (given.deployment.mode === 'training') return trainingEndScreen(ctx, given);
  // Practice changes nothing, and the Ranger of the Day is on no roster: whatever record the API
  // reports for either is not a ranger to show, so both get the debrief and nothing else.
  const mode = given.deployment.mode;
  const input: AftermathInput = mode === 'practice' || mode === 'daily' ? { ...given, aftermath: null } : given;
  const f = frame('aftermath', 'AFTER-ACTION REPORT', [{ keys: 'ENTER', label: 'CONTINUE' }]);
  const unlisted = mode === 'daily' ? 'DAILY DROP - THE ONE ATTEMPT' : 'PRACTICE - NOTHING AT STAKE';
  const who = input.aftermath === null ? unlisted : serviceName(input.aftermath.ranger);
  f.info.append(el('span', '', who), el('span', '', ctx.describe(input.deployment.params.archetype).name));

  const timers = new Timers();
  const env: StepEnv = {
    ctx,
    input,
    timers,
    screen: f.el,
    deceased: isDeceased(input),
    ranger: input.aftermath?.ranger ?? null,
  };
  const names = planSteps(input);
  let index = -1;
  let current: StepView | null = null;
  let done = false;

  const advance = (): void => {
    if (done) return;
    current?.leave?.();
    timers.clear();
    index++;
    const name = names[index];
    if (name === undefined) {
      done = true;
      leave(ctx, input);
      return;
    }
    current = STEPS[name](env, advance);
    f.body.replaceChildren(current.el);
    current.focus?.focus();
  };
  advance();

  return {
    el: f.el,
    back: () => undefined,
    initialFocus: () => current?.focus ?? null,
    dispose: () => {
      done = true;
      timers.clear();
    },
  };
}
